import { Card, DateFormat, Number, Space, Text } from "components/common"
import { Fragment, useEffect, useMemo, useRef, useState } from "react"

import cx from "clsx"
import { AIBadgeScore } from "components/common/aiBdgeScore/aiBadgeScore"
import CardTag from "components/common/cardTag/cardTag"
import DataTable from "components/common/dataTable/dataTable"
import Recording from "components/common/recording/recording"
import CallDrawer from "features/callRecording/components/callDrawer/callDrawer"
import { t } from "i18next"
import { convertToReadableString, localisedValue } from "utils"
import styles from "./callsTab.module.less"
import { ActionButtons, InfoList, StatusTag, TitleDescription } from "components/custom"
import TimeFormat from "components/common/timeFormat/timeFormat"
import UserProfileSummary from "features/shared/components/userProfileSummary"
import ClientInfoTooltip from "features/shared/components/clientInfoToolTip/clientInfoToolTip"
import { useLazyGetCallRecordsQuery } from "features/callRecording/api"
import getPrivilege from "utils/privileges/privileges"
import { PAGE_SUBSECTIONS } from "utils/gtm/gtmEventsData"
import ClientNumberButton from "../contactedNumber"
import { useSelector } from "react-redux"
import { callStatuses } from "constants/list"
import { useGetCallTranscriptionQuery } from "features/marketing/callRecording/api"
import syncIconPopup from "components/custom/syncIconPopup/syncIconPopup"
import { useGetCallDetailQuery } from "features/clients/detail/api"

const getcolumns = (hideColumns, from) => [
  { title: t("Date & Time"), dataIndex: "dateAndTime", key: "dateAndTime", className: "tableStartSpace", width: 150 },
  { title: t("Staff"), dataIndex: "staff", key: "staff", width: 150 },
  ...(!!!hideColumns
    ? [
        { title: t("Client"), dataIndex: "client", key: "client" },
        { title: t("Status"), dataIndex: "status", key: "status" },
        { title: t("Duration"), dataIndex: "duration", key: "duration" },
      ]
    : []),
  { title: t("Tag"), dataIndex: "tag", key: "tag", width: 100 },
  { title: t("Recording"), dataIndex: "recording", key: "recording", width: 50 },
  ...(from === "mktg" ? [] : [{ title: t("Call"), dataIndex: "action", key: "call", width: 50 }]),
  { title: t("Odessy AI"), dataIndex: "odessy", key: "call", width: 50 },
]

const staff = element => {
  return (
    <TitleDescription
      title={
        <Space size={3}>
          {localisedValue({ key: "userName", object: element })}&nbsp;
          <UserProfileSummary staffId={element?.userId} />
        </Space>
      }
      description={localisedValue({ key: "callBy", object: element })}
    />
  )
}
const client = (element, from) => {
  const infoList = [
    [t("Name"), element?.client?.name],
    [t("Email"), element?.client?.email || "-"],
    [t("Country"), element?.client?.countryName],
    [t("City"), element?.client?.cityName || "-"],
    [t("Platform"), element?.client?.platform || "-"],
    [t("Campaign"), element?.client?.campaign || "-"],
  ]
  if (from === "mktg") {
    return (
      <>
        <Text>{element?.client?.name}</Text>
        &nbsp;{syncIconPopup(infoList, 350)}
      </>
    )
  }

  return (
    <>
      {localisedValue({ key: "clientName", object: element })}&nbsp;
      <ClientInfoTooltip client={element.clientId} />
    </>
  )
}
const getActionButtons = (data, handleDrawer) => {
  return [
    {
      key: "call",
      Component: () => (
        <ClientNumberButton
          client={data}
          shape="circle"
          from={{ interacted_from: PAGE_SUBSECTIONS.client_listing_page }}
          disabled={!getPrivilege("can_view_client_contact", data?.privileges)}
        />
      ),
    },
  ]
}

const CallsTab = ({
  toggleParentDrawer = () => {},
  getCount = () => {},
  removeTabs = false,
  hideColumns = false,
  params,
  from,
  useQuery = useLazyGetCallRecordsQuery,
  onClickClient,
  ...props
}) => {
  const userPrivileges = useSelector(state => state.app.privileges)
  const [selectedCall, setSelectedCall] = useState(null)
  const [queryObj, setQueryObj] = useState({
    page: 0,
    pageLimit: 10,
    callType: "all",
    ...params,
  })

  useEffect(() => {
    setQueryObj({
      page: 0,
      pageLimit: 10,
      callType: "all",
      ...params,
    })
  }, [params])

  const callDrawerRef = useRef()
  const [getCallRecords, { data, isFetching, error, refetch }] = useQuery()

  useEffect(() => {
    !!(queryObj?.clientId || queryObj?.id) && getCallRecords(queryObj, true)
  }, [queryObj])

  const onChangePage = (page, pageSize) => {
    setQueryObj(prev => ({ ...prev, pageLimit: pageSize, page: page }))
  }

  const handleCallTrasnscription = call => {
    setSelectedCall(call)
    callDrawerRef.current?.toggleDrawer()
  }
  useEffect(() => {
    data?.count && getCount(data?.count)
  }, [data?.count])

  const callTabs = useMemo(
    () =>
      data &&
      Object.keys(data?.phoneCallRecordsStatistics)?.map(e => {
        return {
          tab: (
            <Fragment key={e}>
              {convertToReadableString(data?.callTabNames[e], true)}{" "}
              <Text type="secondaryLight">
                <Number
                  wrapValue={val => `(${val})`}
                  value={data.phoneCallRecordsStatistics?.[e]}
                  defaultValue="0"
                  digitAfterFraction={1}
                  isStringOnly
                  tooltip
                />
              </Text>
            </Fragment>
          ),
          key: e,
        }
      }),
    [data]
  )
  const getCallsData = (data, handleDrawer) => {
    let dataSource =
      data &&
      data?.phoneCallRecords?.map(element => {
        return {
          dateAndTime: (
            <TitleDescription
              title={<DateFormat date={element.date} dateClassName="" />}
              gapSpace={4}
              description={<TimeFormat date={element.time} isTimeOnlyStr />}
            />
          ),
          staff: staff(element),
          client: client(element, from),
          status: <StatusTag data={callStatuses()[element?.status]} />,

          duration: element?.duration,
          tag: (
            <>
              {!!element?.tagShort ? (
                <CardTag
                  title={element?.tagShort}
                  color={element.tagStyle?.bgColor}
                  tooltipTitle={element?.tag?.tooltipTitle}
                />
              ) : (
                "-"
              )}
              {!!hideColumns ? (
                <>
                  &nbsp;
                  <InfoList
                    list={[
                      [t("Duration"), element?.duration],
                      [t("Status"), <StatusTag data={callStatuses[element?.status]} style={{ "min-width": "10px" }} />],
                    ]}
                  />
                </>
              ) : (
                ""
              )}
            </>
          ),
          recording: (
            <>
              <div style={{ height: 60, display: "flex", alignItems: "center", width: 250 }}>
                {element?.url ? (
                  <Recording id={element.date} recordingSrc={element?.url} />
                ) : (
                  <Text>{t("Recording not available")}</Text>
                )}
              </div>
            </>
          ),

          action: (
            <ActionButtons
              list={getActionButtons(element)}
              animate={false}
              style={{ "--hover-displacement": "-24px" }}
            />
          ),
          odessy: element?.analysisStatus === "completed" && (
            <>
              <AIBadgeScore
                totalScore={element?.assessment?.scoring?.total_score}
                handleDrawer={() => handleDrawer(element)}
                className={cx(styles.analyzerBox, "pointer")}
              />
            </>
          ),
        }
      })
    return dataSource
  }

  return (
    <>
      <Card
        tabList={!removeTabs && callTabs}
        activeTabKey={queryObj?.callType || "all"}
        onTabChange={key => setQueryObj({ ...queryObj, callType: key, page: 0 })}
        bodyStyle={{ padding: `${!!removeTabs ? 0 : "25px 0 0"}` }}
        style={{ flexGrow: 1 }}
      >
        <DataTable
          tableLayout="auto"
          pagination={{ totalCount: data?.count, pageOptions: [10, 25, 50] }}
          paginationOnBottom={true}
          paginationOnTop={false}
          onChangePage={onChangePage}
          columns={getcolumns(hideColumns, from)}
          data={getCallsData(data, handleCallTrasnscription)}
          loading={isFetching}
          error={error}
          onErrorRetry={refetch}
          noUrlPush={true}
          {...props}
        />

        <CallDrawer
          selectedCall={selectedCall}
          useQuery={from === "mktg" ? useGetCallTranscriptionQuery : useGetCallDetailQuery}
          from={from}
          onBack={toggleCallDrawer => {
            toggleCallDrawer()
            setSelectedCall(null)
          }}
          ref={callDrawerRef}
          canAddFeedback={from !== "mktg" && getPrivilege("can_add_feedback", userPrivileges)}
        />
      </Card>
    </>
  )
}

export default CallsTab
